import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Shield, Lock, Mail, Loader2, AlertTriangle, Clock } from "lucide-react";
import MFALoginModal from "../components/modals/MFALoginModal";
import { checkLoginRateLimit, recordFailedAttempt, resetFailedAttempts } from "../services/rateLimitService";
import { logSecurityEvent } from "../services/AuditLogger";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);
  const { login, mfaPending, mfaDetails, completeMfaLogin, cancelMfaLogin } = useAuth();

  // Lockout countdown timer
  useEffect(() => {
    if (lockoutRemaining > 0) {
      const timer = setInterval(() => {
        setLockoutRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setError("");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [lockoutRemaining]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    // Check rate limit status
    const rateStatus = checkLoginRateLimit(email);
    if (rateStatus.isLocked) {
      setLockoutRemaining(rateStatus.remainingSeconds);
      setError(`Account temporarily locked due to consecutive failed attempts. Please try again in ${rateStatus.remainingSeconds} seconds.`);
      return;
    }

    try {
      setLoading(true);
      await login(email, password);
      resetFailedAttempts(email);
    } catch (err) {
      const failedResult = recordFailedAttempt(email);
      if (failedResult.isLocked) {
        setLockoutRemaining(failedResult.remainingSeconds);
        logSecurityEvent({
          action: "AUTH_BRUTE_FORCE_DETECTED",
          category: "AUTH",
          userEmail: email,
          status: "WARNING",
          severity: "CRITICAL",
          details: { attempts: failedResult.attempts, lockoutSeconds: failedResult.remainingSeconds }
        });
        setError(`Maximum failed attempts exceeded (5). Account is temporarily locked for 15 minutes.`);
      } else {
        const remaining = 5 - failedResult.attempts;
        setError(
          remaining <= 2 
            ? `Invalid credentials. Warning: ${remaining} attempt(s) remaining before temporary lockout.` 
            : "Failed to sign in. Please check your credentials."
        );
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  }



  return (
    <div className="login-container">
      <div className="login-card animate-slide-up">
        <div className="login-header">
          <div className="login-logo">
            <img src="/favicon.png" alt="Logo" style={{ width: "48px", height: "48px", borderRadius: "8px", objectFit: "cover" }} />
          </div>
          <h1>Atlas Command</h1>
          <p>Enterprise Management Portal</p>
        </div>

        {error && (
          <div className="login-error-alert animate-fade-in">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label className="form-label" htmlFor="email">Email Address</label>
            <div className="input-with-icon">
              <Mail size={18} className="input-icon" />
              <input
                id="email"
                type="email"
                required
                className="form-input icon-padding"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">Password</label>
            <div className="input-with-icon">
              <Lock size={18} className="input-icon" />
              <input
                id="password"
                type="password"
                required
                className="form-input icon-padding"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading || lockoutRemaining > 0}
          >
            {loading ? (
              <>
                <Loader2 className="spinner-icon" size={18} />
                Signing In...
              </>
            ) : lockoutRemaining > 0 ? (
              <>
                <Clock size={16} style={{ marginRight: "0.5rem" }} />
                Locked ({Math.floor(lockoutRemaining / 60)}m {lockoutRemaining % 60}s)
              </>
            ) : (
              "Sign In"
            )}
          </button>

        </form>

        <div className="login-footer">
          <p>© 2026 Atlas Command. All rights reserved.</p>
        </div>
      </div>

      <MFALoginModal
        isOpen={mfaPending}
        userEmail={mfaDetails?.email || email}
        mfaSecret={mfaDetails?.secret || ""}
        mfaBackupCodes={mfaDetails?.backupCodes || []}
        onSuccess={completeMfaLogin}
        onCancel={cancelMfaLogin}
      />

      <style>{`
        .login-container {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          width: 100vw;
          background-color: var(--bg-main);
          background-image: radial-gradient(circle at 30% 30%, hsla(var(--hue-primary), 89%, 60%, 0.08) 0%, transparent 50%),
                            radial-gradient(circle at 70% 70%, hsla(var(--hue-accent), 83%, 60%, 0.08) 0%, transparent 50%);
          padding: 1.5rem;
        }

        .login-card {
          background-color: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: 2.5rem;
          width: 100%;
          max-width: 440px;
          box-shadow: var(--shadow-lg);
          position: relative;
        }

        .login-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .login-logo {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 64px;
          height: 64px;
          border-radius: var(--radius-md);
          background: linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%);
          margin-bottom: 1rem;
          box-shadow: var(--shadow-primary);
        }

        .logo-icon {
          color: #ffffff;
        }

        .login-header h1 {
          font-size: 1.75rem;
          margin-bottom: 0.25rem;
          background: linear-gradient(135deg, #ffffff 40%, var(--text-secondary) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .login-header p {
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .login-error-alert {
          background-color: var(--danger-light);
          border: 1px solid var(--danger);
          color: var(--danger);
          padding: 0.75rem 1rem;
          border-radius: var(--radius-sm);
          font-size: 0.85rem;
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .input-with-icon {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          color: var(--text-muted);
        }

        .icon-padding {
          padding-left: 2.75rem !important;
        }

        .btn-block {
          width: 100%;
          margin-top: 0.75rem;
          height: 46px;
        }

        .spinner-icon {
          animation: spin 1s linear infinite;
          margin-right: 0.5rem;
        }

        .login-footer {
          margin-top: 2rem;
          text-align: center;
          color: var(--text-muted);
          font-size: 0.75rem;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
